import React from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { formatDateShort, formatTime } from "@/lib/date";
import { TID } from "@/lib/testIds";

/**
 * HeroEditorial — new editorial hero card with image 60–65% and text
 * 35–40%. Two composition modes:
 *
 *   - "side"    Image on the left/right, text panel on the opposite side
 *               (works when the hero has space next to it).
 *   - "overlay" Image full width with a subtle bottom gradient. Text sits
 *               inside a slim bottom band, image protagonista.
 *
 * Props:
 *   article
 *   mode         "side" | "overlay"    default "side"
 *   imageRight   bool                  when mode="side", image on the right
 *   heightClass  Tailwind override for min-height (e.g. "min-h-[520px]")
 */
export default function HeroEditorial({
  article,
  mode = "side",
  imageRight = false,
  heightClass = "min-h-[380px] lg:min-h-[480px]",
}) {
  if (!article) return null;
  const focal = article.image_focal_point || "50% 40%";
  const kicker = article.main_category_name;
  const date = article.published_at ? formatDateShort(article.published_at) : "";
  const time = article.published_at ? formatTime(article.published_at) : "";

  if (mode === "overlay") {
    return (
      <article data-testid={TID.heroArticle} className="relative overflow-hidden bg-nightblue text-white group">
        <Link to={`/noticia/${article.slug}`} className="block">
          <div className={`relative ${heightClass} overflow-hidden bg-blueblack`}>
            <img
              src={article.main_image}
              alt={article.title}
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover card-hover-image"
              style={{ objectPosition: focal }}
            />
            {/* Bottom-only dark gradient — protects the white title without
                covering the photo. Upper half stays clean. Colors align with
                the institutional deep blue (rgba(5,20,35,α)). */}
            <div
              className="absolute inset-x-0 bottom-0 h-[55%] pointer-events-none"
              style={{
                background:
                  "linear-gradient(to top, rgba(5,20,35,0.90) 0%, rgba(5,20,35,0.65) 35%, rgba(5,20,35,0.20) 65%, rgba(5,20,35,0) 100%)",
              }}
            />
            {/* Content pinned to bottom-left as a single visual unit. */}
            <div className="absolute inset-0 flex flex-col justify-end px-5 pb-6 sm:px-8 sm:pb-8 lg:pl-10 lg:pr-10 lg:pb-12">
              <div className="max-w-4xl">
                <span
                  className="block text-[11px] sm:text-[12px] uppercase tracking-[0.18em] font-bold text-watergreen"
                  style={{ textShadow: "0 2px 6px rgba(5,20,35,0.55)" }}
                >
                  {kicker}
                </span>
                <h1
                  className="mt-2 font-serif font-black text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] line-clamp-3"
                  style={{ textShadow: "0 3px 10px rgba(5,20,35,0.65), 0 1px 2px rgba(5,20,35,0.45)" }}
                >
                  {article.title}
                </h1>
                {article.summary && (
                  <p
                    className="mt-3 hidden sm:block text-sm sm:text-base text-white/90 leading-relaxed line-clamp-2"
                    style={{ textShadow: "0 2px 6px rgba(5,20,35,0.55)" }}
                  >
                    {article.summary}
                  </p>
                )}
                <div
                  className="mt-3 text-[11px] uppercase tracking-widest text-white/80 flex flex-wrap items-center gap-x-4"
                  style={{ textShadow: "0 2px 5px rgba(5,20,35,0.55)" }}
                >
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {date} · {time} hs</span>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  // mode === "side" — image + text in independent columns (image 60/65, text 35/40)
  return (
    <article
      data-testid={TID.heroArticle}
      className={`grid grid-cols-1 lg:grid-cols-8 overflow-hidden bg-white border border-softblue group ${heightClass}`}
    >
      <Link
        to={`/noticia/${article.slug}`}
        className={`relative overflow-hidden bg-blueblack ${imageRight ? "lg:col-span-5 lg:order-2" : "lg:col-span-5"}`}
      >
        <img
          src={article.main_image}
          alt={article.title}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover card-hover-image"
          style={{ objectPosition: focal }}
        />
      </Link>
      <div
        className={`p-5 sm:p-6 lg:p-8 flex flex-col justify-center bg-white ${
          imageRight ? "lg:col-span-3 lg:order-1" : "lg:col-span-3"
        }`}
      >
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-turquoise">{kicker}</span>
        <Link to={`/noticia/${article.slug}`} className="block mt-2">
          <h1 className="font-serif font-black text-blueblack text-2xl sm:text-3xl lg:text-4xl leading-[1.1] group-hover:text-petroleum transition-colors">
            {article.title}
          </h1>
        </Link>
        {article.summary && (
          <p className="mt-3 text-sm sm:text-base text-textgray leading-relaxed line-clamp-3">
            {article.summary}
          </p>
        )}
        <div className="mt-4 text-[11px] uppercase tracking-widest text-textgray flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {date} · {time} hs</span>
        </div>
      </div>
    </article>
  );
}
